// Community -> Live Activity -> INDIVIDUAL VISITOR & USER ACTIVITY -> [BY USER].
// One row per Elakay account. An account groups ONLY the browser records it
// actually signed in on and the sessions that carry its user id: anonymous
// activity (before sign-in on a never-used browser, after a logout) is never
// attributed to it, and nothing is inferred from IP, device or location.
// Each account's browsers, sessions and login/logout timeline are loaded
// when the row is expanded. Same collections as [BY VISITOR], no copy.
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { getIndividualAccountDetailAPI, getIndividualAccountsAPI } from "../../../auth/liveApi";
import { roleDisplayLabel } from "../../../../utils/roleDisplayLabel";
import { formatCount } from "../LiveParts";
import { durationLabel, formatInZone, formatTime, shortId } from "./individualFormat";
import { StatusBadge } from "./IndividualBadges";
import { EVENT_LABELS } from "./individualFields";
import { SESSION_STATUS } from "./VisitorDetail";

export const ACCOUNT_SORTS = ["lastActivity", "firstSeen", "browsers"];

const AccountBadge = ({ row, t }) => (
  row.isOnline
    ? <StatusBadge status="online" t={t} />
    : <span className={`ia-badge ${row.accountStatus === "signed_in" ? "type-authenticated" : "type-signed_out"}`}>
      {row.accountStatus === "signed_in" ? t("vAuthenticated", "Signed in") : t("vSignedOut", "Signed out")}
    </span>
);

const END_REASONS = (t) => ({
  explicit_logout: t("endLogout", "Explicit logout"),
  inferred_timeout: t("endInferredShort", "Inactivity timeout (end time unknown)"),
  identity_change: t("endIdentity", "The signed-in account changed or signed out on this browser"),
});

// Account timelines span several days and browsers: full date + browser id.
const AccountTimelineItem = ({ e, tz, t }) => (
  <li className={`ia-tl-item ${e.type === "login_success" ? "k-login" : e.type.startsWith("session_") ? `k-session${e.inferred ? " k-inferred" : ""}` : ""}`}>
    <div className="ia-tl-line"><span className="ia-tl-time">{formatInZone(e.at, tz)}</span><span className="ia-tl-label">{EVENT_LABELS[e.type] || e.type}</span></div>
    <div className="ia-tl-meta">
      <span>{t("browser", "Browser")} <span className="ia-id" title={e.visitorId}>{shortId(e.visitorId)}</span></span>
      {e.sessionId && <span>{t("session", "Session")} <span className="ia-id">{shortId(e.sessionId)}</span></span>}
      {e.endReason && <span>{END_REASONS(t)[e.endReason] || e.endReason}</span>}
      {e.method === "device_verification" && <span>{t("viaCode", "via new-device code")}</span>}
    </div>
  </li>
);

const AccountDetail = ({ userId, t, onOpenVisitor }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [more, setMore] = useState(false);
  const load = useCallback((before) => {
    if (before) setMore(true); else setState((s) => ({ ...s, loading: true, error: null }));
    getIndividualAccountDetailAPI(userId, before ? { before } : {})
      .then((data) => setState((s) => ({
        loading: false,
        error: null,
        data: before && s.data ? { ...s.data, sessions: [...s.data.sessions, ...data.sessions], sessionsNextBefore: data.sessionsNextBefore } : data,
      })))
      .catch((e) => setState((s) => ({ ...s, loading: false, error: e.message || t("loadError", "Could not load the activity table.") })))
      .finally(() => setMore(false));
  }, [userId, t]);
  useEffect(() => { load(null); }, [load]);

  if (state.error) return <div className="ia-state is-error" role="alert"><span>{state.error}</span><button type="button" className="live-btn live-btn-ghost" onClick={() => load(null)}>{t("retry", "Retry")}</button></div>;
  if (!state.data) return <div className="ia-state" aria-busy="true"><div className="ia-skeleton" style={{ width: "70%" }} /><span className="live-sr">{t("loading", "Loading…")}</span></div>;
  const d = state.data;
  const tz = d.timezone;
  const labels = SESSION_STATUS(t);
  return (
    <div className="ia-account-detail">
      <h4 className="ia-subhead">{t("accBrowsers", "Browsers this account signed in on")}</h4>
      <div className="ia-table-wrap"><table className="ia-table">
        <thead><tr><th>{t("visitorId", "Visitor ID")}</th><th>{t("cDevice", "Device")}</th><th>{t("cLocation", "Location")}</th><th>{t("cStatus", "Status")}</th><th>{t("lastActivity", "Last recorded activity")}</th><th aria-label={t("actions", "Actions")} /></tr></thead>
        <tbody>{d.visitors.map((v) => (
          <tr key={v.visitorId}>
            <td data-label={t("visitorId", "Visitor ID")}>
              <span className="ia-id" title={v.visitorId}>{shortId(v.visitorId)}</span>
              {v.associatedAccounts?.length > 1 && <small className="live-muted"> · {t("sharedBrowser", "shared with {value0} other account(s)", { value0: v.associatedAccounts.length - 1 })}</small>}
            </td>
            <td data-label={t("cDevice", "Device")}>{[v.device?.deviceType, v.device?.os, v.device?.browser].filter(Boolean).join(" · ") || "—"}</td>
            <td data-label={t("cLocation", "Location")}>{[v.geo?.city, v.geo?.state, v.geo?.country].filter((x) => x && x !== "Unknown").join(", ") || "—"}</td>
            <td data-label={t("cStatus", "Status")}>{v.signedInHere ? (v.isOnline ? <StatusBadge status="online" t={t} /> : t("vAuthenticated", "Signed in")) : t("notSignedInHere", "Not signed in here now")}</td>
            <td data-label={t("lastActivity", "Last recorded activity")}>{formatInZone(v.lastActivityAt, tz)}</td>
            <td><button type="button" className="live-btn live-btn-ghost" onClick={() => onOpenVisitor(v.visitorId)}>{t("openBrowser", "Open browser record")}</button></td>
          </tr>
        ))}</tbody>
      </table></div>

      <h4 className="ia-subhead">{t("accSessions", "Sessions of this account")}{d.sessionCount !== undefined ? ` (${formatCount(d.sessionCount)})` : ""}</h4>
      {d.fields?.totalSessions === false ? <p className="ia-note">{t("fieldOff", "This field is turned off")}</p> : !d.sessions.length ? <p className="ia-note">{t("noSessions", "No session recorded.")}</p> : (
        <div className="ia-table-wrap"><table className="ia-table">
          <thead><tr><th>{t("started", "Started")}</th><th>{t("lastObserved", "Last observed activity")}</th><th>{t("cStatus", "Status")}</th><th>{t("visitorId", "Visitor ID")}</th><th className="num">{t("cPageViews", "Page views")}</th>{d.fields?.historyPage && <th>{t("recentPages", "Recent pages")}</th>}</tr></thead>
          <tbody>{d.sessions.map((s) => (
            <tr key={s.sessionId}>
              <td data-label={t("started", "Started")}>{formatInZone(s.startedAt, tz)}</td>
              <td data-label={t("lastObserved", "Last observed activity")}>{formatInZone(s.lastSeenAt, tz)} <small className="live-muted">{durationLabel(s.startedAt, s.endedAt || s.lastSeenAt)}</small></td>
              <td data-label={t("cStatus", "Status")}>{labels[s.status] || s.status}{s.endedAt ? <small className="live-muted"> · {formatTime(s.endedAt, tz)}</small> : null}</td>
              <td data-label={t("visitorId", "Visitor ID")}><span className="ia-id" title={s.visitorId}>{shortId(s.visitorId)}</span></td>
              <td data-label={t("cPageViews", "Page views")} className="num">{s.totals?.pageViews !== undefined ? formatCount(s.totals.pageViews) : "—"}</td>
              {d.fields?.historyPage && <td data-label={t("recentPages", "Recent pages")}><small>{(s.recentRoutes || []).slice(-5).join(" → ") || "—"}</small></td>}
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {d.sessionsNextBefore && <button type="button" className="live-btn live-btn-ghost" style={{ marginTop: 8 }} disabled={more} onClick={() => load(d.sessionsNextBefore)}>{t("loadMore", "Load more")}</button>}

      <h4 className="ia-subhead">{t("accTimeline", "Login, logout and session timeline")}</h4>
      {d.historyHidden ? <p className="ia-note">{t("fieldOff", "This field is turned off")}</p> : !d.timeline.length ? <p className="ia-note">{t("noEvents", "No event recorded.")}</p> : (
        <ol className="ia-timeline">{d.timeline.map((e, i) => (
          <AccountTimelineItem key={`${e.at}-${e.type}-${i}`} e={e} tz={tz} t={t} />
        ))}</ol>
      )}
      <p className="ia-note">{t("accRule", "Only activity recorded while this account was signed in is shown. Browsing before signing in on a new browser, or after signing out, stays anonymous.")}</p>
    </div>
  );
};

const AccountActivityTable = ({ filters, limit, tick, t, onOpenVisitor }) => {
  const [sort, setSort] = useState({ sort: "lastActivity", dir: "desc" });
  const [page, setPage] = useState(1);
  const [res, setRes] = useState({ loading: false, error: null, data: null });
  const [expanded, setExpanded] = useState(() => new Set());
  const [retry, setRetry] = useState(0);

  // Any filter or sort change restarts from page 1.
  useEffect(() => { setPage(1); setExpanded(new Set()); }, [filters, limit, sort]);

  useEffect(() => {
    const controller = new AbortController();
    setRes((r) => ({ ...r, loading: true, error: null }));
    const { type, sort: _visitorSort, dir: _visitorDir, ...shared } = filters;
    getIndividualAccountsAPI({ ...shared, ...sort, limit, page }, { signal: controller.signal })
      .then((data) => setRes({ loading: false, error: null, data }))
      .catch((e) => {
        if (e.name === "AbortError") return;
        const msg = e.status === 403 ? t("forbidden", "Reserved to the Developer (Family) role.") : e.message || t("loadError", "Could not load the activity table.");
        setRes((r) => ({ ...r, loading: false, error: msg }));
      });
    return () => controller.abort();
  }, [filters, limit, sort, page, tick, retry, t]);

  const data = res.data;
  const fields = data?.fields || {};
  const tz = data?.timezone;
  const onSort = (key) => setSort((s) => (s.sort === key ? { sort: key, dir: s.dir === "asc" ? "desc" : "asc" } : { sort: key, dir: "desc" }));
  const sortHeader = (key, label) => {
    const active = sort.sort === key;
    return (
      <th scope="col" className={key === "browsers" ? "num" : ""} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
        <button type="button" className={`ia-sort ${active ? "is-active" : ""}`} onClick={() => onSort(key)}>{label}{active ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}</button>
      </th>
    );
  };
  const toggle = (id) => setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const cols = 3 + (fields.firstSeen ? 1 : 0) + (fields.lastSeen ? 1 : 0) + (fields.totalSessions ? 1 : 0) + (fields.totalPageViews ? 1 : 0) + (fields.loginCount ? 1 : 0) + (fields.historyPage ? 1 : 0);
  const start = data && data.rows.length ? (data.page - 1) * data.limit + 1 : 0;
  const end = data ? (data.page - 1) * data.limit + data.rows.length : 0;

  return (
    <>
      <div className="ia-toolbar">
        <span className="live-muted" aria-live="polite">
          {data ? t("showingAccounts", "{value0}–{value1} of {value2} accounts", { value0: formatCount(start), value1: formatCount(end), value2: formatCount(data.total) }) : ""}
        </span>
      </div>
      {res.error && <div className="ia-state is-error" role="alert"><strong>{t("errorTitle", "Something went wrong")}</strong><span>{res.error}</span><button type="button" className="live-btn live-btn-ghost" onClick={() => setRetry((n) => n + 1)}>{t("retry", "Retry")}</button></div>}
      {!res.error && !data && res.loading && (
        <div className="ia-state" aria-busy="true"><span className="live-sr">{t("loading", "Loading…")}</span>{[1, 2, 3].map((i) => <div key={i} className="ia-skeleton" style={{ width: `${90 - i * 12}%` }} />)}</div>
      )}
      {!res.error && data && !data.rows.length && (
        <div className="ia-state">
          <strong>{t("emptyAccounts", "No account matches")}</strong>
          <span>{t("emptyAccountsHint", "Accounts appear here once someone signs in on a recorded browser. Anonymous visitors are listed in By visitor.")}</span>
        </div>
      )}
      {!res.error && data && data.rows.length > 0 && (
        <div className="ia-table-wrap" aria-busy={res.loading}>
          <table className="ia-table">
            <caption className="live-sr">{t("captionAccounts", "Individual activity, one row per account")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("cAccount", "Account")}</th>
                <th scope="col">{t("cStatus", "Status")}</th>
                {sortHeader("browsers", t("cBrowsers", "Browsers"))}
                {fields.totalSessions && <th scope="col" className="num">{t("cSessions", "Sessions")}</th>}
                {fields.totalPageViews && <th scope="col" className="num">{t("cPageViews", "Page views")}</th>}
                {fields.loginCount && <th scope="col" className="num">{t("cLogins", "Logins")}</th>}
                {fields.firstSeen && sortHeader("firstSeen", t("cFirstActivity", "First activity"))}
                {fields.lastSeen && sortHeader("lastActivity", t("cLastActivity", "Last activity"))}
                {fields.historyPage && <th scope="col">{t("recentPages", "Recent pages")}</th>}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => {
                const isOpen = expanded.has(row.userId);
                return (
                  <Fragment key={row.userId}>
                    <tr className={`ia-row ${isOpen ? "is-expanded" : ""}`} tabIndex={0} onClick={() => toggle(row.userId)} onKeyDown={(e) => { if (e.key === "Enter") toggle(row.userId); }}>
                      <td data-label={t("cAccount", "Account")}>
                        <div style={{ display: "flex", alignItems: "flex-start" }}>
                          <button type="button" className="ia-caret" aria-expanded={isOpen} aria-label={isOpen ? t("collapse", "Collapse details") : t("expand", "Expand details")} onClick={(e) => { e.stopPropagation(); toggle(row.userId); }}>
                            {isOpen ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                          </button>
                          <div className="ia-cell-main">
                            <span>{row.name}</span>
                            <small>{row.email}{row.role ? ` · ${roleDisplayLabel(row.role)}` : ""}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label={t("cStatus", "Status")}><AccountBadge row={row} t={t} /></td>
                      <td data-label={t("cBrowsers", "Browsers")} className="num">{formatCount(row.browsers)}{row.signedInVisitorIds?.length ? <small className="live-muted"> ({formatCount(row.signedInVisitorIds.length)} {t("signedInNow", "signed in")})</small> : null}</td>
                      {fields.totalSessions && <td data-label={t("cSessions", "Sessions")} className="num">{formatCount(row.sessions)}</td>}
                      {fields.totalPageViews && <td data-label={t("cPageViews", "Page views")} className="num">{formatCount(row.pageViews)}</td>}
                      {fields.loginCount && <td data-label={t("cLogins", "Logins")} className="num">{formatCount(row.logins?.success)}</td>}
                      {fields.firstSeen && <td data-label={t("cFirstActivity", "First activity")}>{formatInZone(row.firstActivityAt, tz)}</td>}
                      {fields.lastSeen && <td data-label={t("cLastActivity", "Last activity")}>{formatInZone(row.lastActivityAt, tz)}</td>}
                      {fields.historyPage && <td data-label={t("recentPages", "Recent pages")}><small>{(row.recentPages || []).slice(0, 4).join(", ") || "—"}</small></td>}
                    </tr>
                    {isOpen && <tr className="ia-expand"><td colSpan={cols}><AccountDetail userId={row.userId} t={t} onOpenVisitor={onOpenVisitor} /></td></tr>}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && data.totalPages > 1 && (
        <nav className="ia-pager" aria-label={t("pagination", "Pagination")}>
          <button type="button" className="live-btn live-btn-ghost" disabled={page <= 1 || res.loading} onClick={() => setPage((p) => p - 1)}>{t("previous", "Previous")}</button>
          <span className="live-muted">{t("pageOf", "Page {value0} of {value1}", { value0: data.page, value1: data.totalPages })}</span>
          <button type="button" className="live-btn live-btn-ghost" disabled={page >= data.totalPages || res.loading} onClick={() => setPage((p) => p + 1)}>{t("next", "Next")}</button>
        </nav>
      )}
    </>
  );
};

export default AccountActivityTable;
